import { Navigate, useSearchParams } from 'react-router-dom'
import { cloneElement, useMemo } from 'react'
import { useActiveWorkspace, useActiveProject } from '@/state/selection.store'
import { usePermissions } from '@/hooks/usePermissions'
import NotFoundPage from '@/components/error/NotFoundPage'
import { PageLoader } from '@/components/ui/PinwheelLoader'
import { ACCOUNTS_URL } from '@/lib/constants'
import { safeLocalStorage } from '@/lib/safe-storage'

/**
 * Authentication guard - checks if user is logged in
 * Redirects to external accounts service if not authenticated
 */
export function AuthGuard({ children }) {
  const token = safeLocalStorage.getItem('auth_token')
  if (!token) {
    const redirectUrl = window.location.origin + window.location.pathname + window.location.search
    window.location.href = `${ACCOUNTS_URL}/login?redirect=${encodeURIComponent(redirectUrl)}`
    return null
  }
  return children
}

function useAccessCheck({ permission, any, all, role }) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasRole } = usePermissions()

  return useMemo(() => {
    if (permission) return hasPermission(permission)
    if (any) return hasAnyPermission(any)
    if (all) return hasAllPermissions(all)
    if (role) return hasRole(role)
    return true
  }, [permission, any, all, role, hasPermission, hasAnyPermission, hasAllPermissions, hasRole])
}

/**
 * Role-based access guard
 */
export function RoleGuard({ children, allowedRoles = [] }) {
  const { hasRole, isLoading } = usePermissions()

  if (isLoading) {
    return <PageLoader text={null} />
  }

  if (allowedRoles.length === 0) {
    return children
  }

  if (!hasRole(allowedRoles)) {
    return <NotFoundPage />
  }

  return children
}

export function PermissionGuard({
  children,
  permission,
  any,
  all,
  role,
  fallback = null,
  hide = false
}) {
  const hasAccess = useAccessCheck({ permission, any, all, role })

  if (!hasAccess) {
    return hide ? null : fallback
  }

  return children
}

export function PermissionDisabled({
  children,
  permission,
  any,
  all,
  role,
  className = ''
}) {
  const hasAccess = useAccessCheck({ permission, any, all, role })

  if (!hasAccess) {
    return (
      <div className={`opacity-50 pointer-events-none ${className}`} style={{ cursor: 'not-allowed' }}>
        {children}
      </div>
    )
  }

  if (children && typeof children === 'object' && 'type' in children) {
    try {
      return cloneElement(children, { ...children.props, disabled: false })
    } catch {
      return children
    }
  }

  return children
}

export function ProtectedRoute({
  children,
  permission,
  any,
  all,
  role,
  fallback = null
}) {
  const { isLoading } = usePermissions()
  const hasAccess = useAccessCheck({ permission, any, all, role })

  if (isLoading) {
    return fallback || <PageLoader text={null} />
  }

  if (!hasAccess) {
    return <NotFoundPage />
  }

  return children
}

export function ProtectedProjectRoute({ children, allowedRoles = [], permission, any }) {
  const [searchParams] = useSearchParams()
  const project = searchParams.get('project')

  // Redirect to workspace root if no project is selected
  if (!project) {
    const workspace = searchParams.get('workspace')
    return <Navigate to={workspace ? `/projects?workspace=${workspace}` : '/projects'} replace />
  }

  return (
    <AuthGuard>
      {permission || any ? (
        <ProtectedRoute permission={permission} any={any}>
          {children}
        </ProtectedRoute>
      ) : (
        <RoleGuard allowedRoles={allowedRoles}>
          {children}
        </RoleGuard>
      )}
    </AuthGuard>
  )
}

export function ProtectedWorkspaceRoute({ children, allowedRoles = [], permission, any }) {
  return (
    <AuthGuard>
      {permission || any ? (
        <ProtectedRoute permission={permission} any={any}>
          {children}
        </ProtectedRoute>
      ) : (
        <RoleGuard allowedRoles={allowedRoles}>
          {children}
        </RoleGuard>
      )}
    </AuthGuard>
  )
}

export function ProtectedRouteWithAuth({ children, allowedRoles = [], permission }) {
  return (
    <AuthGuard>
      {permission ? (
        <ProtectedRoute permission={permission}>
          {children}
        </ProtectedRoute>
      ) : (
        <RoleGuard allowedRoles={allowedRoles}>
          {children}
        </RoleGuard>
      )}
    </AuthGuard>
  )
}

export function useRouteContext() {
  const [searchParams] = useSearchParams()
  const { workspaceId, hasWorkspace } = useActiveWorkspace()
  const { projectId, hasProject } = useActiveProject()

  const urlWorkspace = searchParams.get('workspace')
  const urlProject = searchParams.get('project')

  return useMemo(() => ({
    urlWorkspace,
    urlProject,
    workspaceId,
    projectId,
    hasWorkspace,
    hasProject,
    isWorkspaceValid: !!urlWorkspace,
    isProjectValid: !!urlProject,
    needsWorkspaceRedirect: !urlWorkspace,
    needsProjectRedirect: !!urlWorkspace && !urlProject,
  }), [urlWorkspace, urlProject, workspaceId, projectId, hasWorkspace, hasProject])
}
